/**
 * How the rows of an actor table get their ids. Every table must declare it (a test fails on a
 * table that does not), because it decides whether two devices can create the same row twice:
 *
 * - `singleton`: one row per database, with a constant id.
 * - `derived`: the id is derived from the row's natural key (`actor-row-ids.ts`, or a readable
 *   key such as `quest:<questId>`), so every device creates the same row.
 * - `random`: a new id per row. Only for events and for things the user creates on purpose, where
 *   two devices creating two rows is the correct result.
 * - `mixed`: the table holds rows of more than one kind; the comment says which.
 *
 * No actor table may have a unique index besides its primary key: sync cannot resolve a unique
 * conflict, so the duplicate has to be impossible by construction instead.
 */
export type ActorTableIdentity = "derived" | "mixed" | "random" | "singleton";

/** Keyed by SQL table name. */
export const ACTOR_TABLE_IDENTITY = {
  // Favorites is a singleton (`FAVORITES_COLLECTION_ID`); a collection the user names is random.
  collection: "mixed",
  collection_morpheme: "derived",
  fsrs_card: "derived",
  fsrs_card_review_log: "random",
  morpheme_note: "derived",
  morpheme_search_visit: "derived",
  // A daily login reward is derived (`daily-login:<day>`); a grant is random.
  reward_claim: "mixed",
  study_session: "random",
  travel_trip: "random",
  user: "singleton",
  user_card: "derived",
  user_card_review: "random",
  user_grapheme_meaning_card: "derived",
  user_grapheme_pronunciation_card: "derived",
  user_quest: "derived",
  user_quiz_card: "derived",
  user_sentence: "derived",
  user_slide: "derived",
  user_word: "derived",
  user_word_sentence_card: "derived",
} as const satisfies Record<string, ActorTableIdentity>;

export type ActorTableName = keyof typeof ACTOR_TABLE_IDENTITY;

/**
 * Columns that hold the id of a row in another table, by SQL table name. Declared here and never
 * as a FOREIGN KEY constraint: Turso Cloud enforces those, the local sync server and the devices
 * do not, and a constraint the cloud copy enforces is a way for it to reject an upload (a child
 * that arrives before its parent, a parent deleted first). The gateway's orphan rule, which reads
 * this, is the one place that decides what happens to a row without its parent (see
 * `database.md`, "Server: sync gateway"). A test fails on any FOREIGN KEY in the schema.
 */
export const ACTOR_TABLE_PARENTS = {
  collection: [{ column: "user_id", table: "user" }],
  user_card: [{ column: "fsrs_card_id", table: "fsrs_card" }],
  user_card_review: [{ column: "card_id", table: "user_card" }],
  user_grapheme_meaning_card: [{ column: "user_card_id", table: "user_card" }],
  user_grapheme_pronunciation_card: [{ column: "user_card_id", table: "user_card" }],
  user_quiz_card: [{ column: "user_card_id", table: "user_card" }],
  user_word_sentence_card: [{ column: "user_card_id", table: "user_card" }],
} as const satisfies Partial<
  Record<ActorTableName, readonly { column: string; table: ActorTableName }[]>
>;
